import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DesktopSellerPageContainer } from '@/components/desktop/DesktopSellerPageContainer';
import { sellerService } from '@/services/seller';
import { useToast } from '@/components/common/Toast';
import { useDiscountProducts } from '@/hooks/discount/useDiscountProducts';
import { DiscountRateGuide } from '@/components/seller/discount/DiscountRateGuide';
import { RegisteredSection } from '@/components/seller/discount/sections/RegisteredSection';
import { AvailableSection } from '@/components/seller/discount/sections/AvailableSection';
import { DiscountSaveButton } from '@/components/seller/discount/DiscountSaveButton';

export function DesktopReservationDiscountPage() {
  const navigate = useNavigate();
  const { ToastContainer, showToast } = useToast();
  const [restaurantId, setRestaurantId] = useState<string | null>(null);
  const [reservationDiscountEnabled, setReservationDiscountEnabled] = useState(false);
  const [isTogglingReservation, setIsTogglingReservation] = useState(false);

  // 레스토랑 ID 가져오기
  useEffect(() => {
    const getRestaurantId = async () => {
      const result = await sellerService.getSellerRestaurantId();
      if (result.success && result.restaurantId) {
        setRestaurantId(result.restaurantId);

        // 예약할인 활성화 상태 조회
        const statusResult = await sellerService.getReservationDiscountStatus(result.restaurantId);
        if (statusResult.success && statusResult.data) {
          setReservationDiscountEnabled(statusResult.data.reservation_discount_enabled);
        }
      } else {
        showToast(result.error || '레스토랑 정보를 찾을 수 없습니다');
      }
    };
    getRestaurantId();
  }, [showToast]);

  // 예약할인 토글 핸들러
  const handleReservationToggle = async (enabled: boolean) => {
    if (!restaurantId || isTogglingReservation) return;

    setIsTogglingReservation(true);
    try {
      const result = await sellerService.toggleReservationDiscount(restaurantId, enabled);
      if (result.success) {
        setReservationDiscountEnabled(enabled);
        showToast(enabled ? '예약할인이 활성화되었습니다' : '예약할인이 비활성화되었습니다');
      } else {
        showToast(result.error || '상태 변경에 실패했습니다');
      }
    } catch (error) {
      showToast('상태 변경 중 오류가 발생했습니다');
    } finally {
      setIsTogglingReservation(false);
    }
  };

  // 할인 상품 관리 훅
  const {
    products,
    registeredProducts,
    availableProducts,
    loading,
    saving,
    error,
    handleDiscountRateChange,
    handleStockChange,
    toggleProductSelection,
    saveProducts,
    selectAllRegistered,
    selectAllAvailable,
    selectedCount,
    canSave
  } = useDiscountProducts({
    restaurantId: restaurantId || '',
    offerType: 'RESERVATION_DISCOUNT'
  });

  // 다음날 영업시간 정보 계산
  const getTomorrowOperatingHours = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dayOfWeek = tomorrow.getDay(); // 0=일요일, 1=월요일, ...
    
    // 임시로 하드코딩된 영업시간 (실제로는 restaurants 테이블에서 가져와야 함)
    const operatingHours = {
      0: { open: '10:00', close: '22:00' }, // 일요일
      1: { open: '09:00', close: '22:00' }, // 월요일
      2: { open: '09:00', close: '22:00' }, // 화요일
      3: { open: '09:00', close: '22:00' }, // 수요일
      4: { open: '09:00', close: '22:00' }, // 목요일
      5: { open: '09:00', close: '23:00' }, // 금요일
      6: { open: '10:00', close: '23:00' }, // 토요일
    };

    const hours = operatingHours[dayOfWeek as keyof typeof operatingHours];
    
    return {
      reservationStart: hours.open,
      reservationEnd: subtractHour(hours.close, 1), // 영업종료 1시간 전
      pickupStart: addHour(hours.open, 1), // 오픈 1시간 후
      pickupEnd: subtractHour(hours.close, 1), // 영업종료 1시간 전
      dayName: ['일', '월', '화', '수', '목', '금', '토'][dayOfWeek]
    };
  };

  const addHour = (timeStr: string, hours: number): string => {
    const [hour, minute] = timeStr.split(':').map(Number);
    const newHour = hour + hours;
    return `${newHour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  };

  const subtractHour = (timeStr: string, hours: number): string => {
    const [hour, minute] = timeStr.split(':').map(Number);
    const newHour = hour - hours;
    return `${newHour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  };

  const tomorrowHours = getTomorrowOperatingHours();

  const handleSave = async () => {
    const result = await saveProducts();
    if (result?.success) {
      showToast('예약할인 제품설정이 저장되었습니다');
      setTimeout(() => {
        navigate('/seller/store');
      }, 3000);
    } else {
      showToast(error || '저장에 실패했습니다');
    }
  };

  if (loading) {
    return (
      <DesktopSellerPageContainer>
        <div className="flex items-center justify-center min-h-[600px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-500 mx-auto mb-6"></div>
            <p className="text-[18px] text-gray-600">상품 목록을 불러오는 중...</p>
          </div>
        </div>
      </DesktopSellerPageContainer>
    );
  }

  return (
    <DesktopSellerPageContainer>
      <div className="max-w-6xl mx-auto">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <button
              onClick={() => navigate('/seller/store')}
              className="mr-4 p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-[32px] font-bold text-gray-800">예약할인 상품설정</h1>
          </div>

          {/* 예약할인 시간 정보 */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-[24px] font-semibold text-gray-800">예약할인 시간 정보</h2>
              
              {/* 예약할인 활성화 토글 */}
              <div className="flex items-center space-x-4">
                <span className={`text-[16px] font-medium ${reservationDiscountEnabled ? 'text-green-700' : 'text-gray-500'}`}>
                  {reservationDiscountEnabled ? '활성화됨' : '비활성화됨'}
                </span>
                <button
                  onClick={() => handleReservationToggle(!reservationDiscountEnabled)}
                  disabled={isTogglingReservation}
                  className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                    reservationDiscountEnabled ? 'bg-green-500' : 'bg-gray-300'
                  } ${isTogglingReservation ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                      reservationDiscountEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* 상태 안내 메시지 */}
            <div className={`p-4 rounded-lg mb-6 border ${
              reservationDiscountEnabled ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
            }`}>
              <p className={`text-[16px] font-medium ${
                reservationDiscountEnabled ? 'text-green-700' : 'text-gray-600'
              }`}>
                {reservationDiscountEnabled 
                  ? '✓ 구매자가 예약할인 상품을 주문할 수 있습니다' 
                  : '⚠ 예약할인이 비활성화되어 구매자가 주문할 수 없습니다'}
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-blue-50 rounded-lg p-6">
                <h3 className="text-[18px] font-semibold text-blue-700 mb-3">예약가능시간 ({tomorrowHours.dayName}요일)</h3>
                <p className="text-[20px] font-bold text-blue-800">
                  {tomorrowHours.reservationStart} ~ {tomorrowHours.reservationEnd}
                </p>
              </div>
              
              <div className="bg-green-50 rounded-lg p-6">
                <h3 className="text-[18px] font-semibold text-green-700 mb-3">수령가능시간 (다음날)</h3>
                <p className="text-[20px] font-bold text-green-800">
                  {tomorrowHours.pickupStart} ~ {tomorrowHours.pickupEnd}
                </p>
              </div>
            </div>
            
            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center">
                <div className="text-blue-500 text-xl mr-3">ℹ️</div>
                <p className="text-[16px] text-blue-700 font-medium">
                  예약할인은 매일 자정부터 적용되며, 다음날 수령하는 상품입니다.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Product Selection */}
          <div className="lg:col-span-2 space-y-8">
            {/* 할인율 안내 */}
            <DiscountRateGuide type="reservation" />

            {/* 등록된 예약할인 상품 */}
            <RegisteredSection
              products={[...registeredProducts, ...products.filter(p => p.selected && !p.isRegisteredInDB)]}
              type="reservation"
              onDiscountRateChange={handleDiscountRateChange}
              onStockChange={handleStockChange}
              onProductSelect={toggleProductSelection}
              onSelectAll={selectAllRegistered}
            />

            {/* 등록 가능한 상품 목록 */}
            <AvailableSection
              products={availableProducts}
              type="reservation"
              onDiscountRateChange={handleDiscountRateChange}
              onStockChange={handleStockChange}
              onProductSelect={toggleProductSelection}
              onSelectAll={selectAllAvailable}
            />
          </div>

          {/* Right Column - Summary & Actions */}
          <div className="space-y-6">
            {/* 선택 요약 */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h3 className="text-[20px] font-semibold text-gray-800 mb-4">선택 요약</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-[16px] text-gray-600">선택된 상품</span>
                  <span className="text-[18px] font-semibold text-gray-800">{selectedCount}개</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[16px] text-gray-600">할인 유형</span>
                  <span className="text-[16px] font-semibold text-blue-600">예약할인</span>
                </div>
              </div>
            </div>

            {/* 할인 안내 */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h3 className="text-[20px] font-semibold text-gray-800 mb-4">예약할인 안내</h3>
              <div className="space-y-3 text-[14px] text-gray-600">
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <p>할인율은 0~10% 범위에서 설정 가능</p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <p>전날 예약 후 다음날 수령하는 방식</p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <p>매일 자정부터 새로운 예약할인 적용</p>
                </div>
              </div>
            </div>

            {/* 저장 버튼 */}
            <div className="sticky top-6">
              <DiscountSaveButton
                type="reservation"
                canSave={canSave}
                saving={saving}
                selectedCount={selectedCount}
                onSave={handleSave}
              />
            </div>
          </div>
        </div>
      </div>

      <ToastContainer />
    </DesktopSellerPageContainer>
  );
}